import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { COLORS } from '../Styles/Colors';
import { AddButton, CloseButton, EditButton, IconActionButton, ModalHeader, ModalOverlay, PageContainer } from '../Styles/AppStyles';
import { IoIosPin } from 'react-icons/io';
import { Role, User } from '../Types';
import useGetData from '../Hooks/useGetData';
import apiClient from '../Services/ApiClient';
import UserForm, { UserFormInput } from './User/UserForm';
import { PencilIcon, Trash2 } from 'lucide-react';
import { decode } from '../Services/DecodeToken';
import styled from 'styled-components';
import { useSnackbar } from '../Context/SnackbarProvider';
import ConfirmationModal from '../Components/Modal/ConfirmationModal';

import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Fade,
  CircularProgress,
  FormControlLabel,
  Avatar,
  Stack,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { Header } from '../Components/Header/Header';
import useGetCustomData from '../Hooks/useGetCustomData';
import { LocationSelect } from '../Components/Select/LocationSelect';
import UserBulkImportComponent from './User/UserBulkImportComponent';

export const ModalContent = styled.div`
  width: 100%;
  max-width: 800px;
  background-color: ${COLORS.white};
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid ${COLORS.lightGray};
  max-height: 85vh;
  min-height: 85vh;
  overflow-y: auto;
  position: relative;
  box-sizing: border-box;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 16px;
    max-height: 92dvh;
    min-height: 0;
    border-radius: 16px 16px 0 0;
  }
`;

const BulkImportLink = styled.button`
  position: absolute;
  top: 80px;
  right: 20px;
  background: none;
  border: none;
  color: ${COLORS.teal};
  font-size: 0.9em;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${COLORS.darkTeal};
  }

  @media (max-width: 899px) {
    position: static;
    padding: 8px 0;
    min-height: 44px;
  }
`;

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;

const UsersPage: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { showSnackbar } = useSnackbar();
  const currentUserId = Number((decode() as any)?.userId);

  const [roles] = useGetData<Role>('/api/roles');

  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [selectedOvs, setSelectedOvs] = useState<string>('');

  const usersUrl = useMemo(
    () => (selectedOvs ? `/api/users?ovid=${selectedOvs}` : '/api/users'),
    [selectedOvs]
  );
  const [users, loading, errorFetchUser, refetch] = useGetCustomData(usersUrl);

 

  const usersWithRoles = React.useMemo(() => {
    const userList = Array.isArray(users) ? users : [];
    if (!userList.length || !roles) return [];

    return userList
      .filter((user: any) => Number(user.IsActive) === 1)
      .map((user: any) => {
        const role = roles.find((r: any) => r.id === user.role_id);
        return {
          ...user,
          role_name: role ? role.name : '',
          id: user.ID,
        };
      });
  }, [users, roles]);



  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      const response = await apiClient(`/api/users/${userToDelete.ID}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to delete user', "error", "Something went wrong");
        return;
      }

      showSnackbar(`Successfully deleted ${userToDelete.first_name}`, "success", "Delete Success");
      refetch();
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err: any) {
      showSnackbar(err.message || "Failed to delete", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setUserToEdit(null);
  };

  const columns: ColumnDef[] = [
  {
    field: 'actions',
    headerName: 'Actions',
    width: 110,
    sortable: false,
    filterable: false,
    renderCell: (params: any) => (
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: "center" }}>
        <EditButton 
          title="Edit user"
          onClick={(e) => {
            e.stopPropagation(); 
            setUserToEdit(params.row);
            setShowAddModal(true);
          }}
        >
          <PencilIcon />
        </EditButton>
        {Number(params.row.ID) !== currentUserId && (
          <IconActionButton
            $variant="danger"
            title="Delete user"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
          >
            <Trash2 size={16} />
          </IconActionButton>
        )}
      </div>
    )
  },
  { field: 'account_name', headerName: 'Account', width: 200, sortable: true, searchable: true },
  { field: 'location_name', headerName: 'Location', width: 200, sortable: true, searchable: true },
  { field: 'username', headerName: 'Username', width: 150, sortable: true, searchable: true },
  { field: 'first_name', headerName: 'First Name', width: 150, sortable: true, searchable: true },
  { field: 'last_name', headerName: 'Last Name', width: 150, sortable: true, searchable: true },
  { field: 'email', headerName: 'Email', width: 200, sortable: true, searchable: true },
  { field: 'contact_number', headerName: 'Mobile Number', width: 150, sortable: true, searchable: true },
  { field: 'RFID', headerName: 'RFID', width: 150, sortable: true, searchable: true },
  { field: 'role_name', headerName: 'Role', width: 120, sortable: true, searchable: true },
  { 
    field: 'IsConsumed', 
    headerName: 'Consumed', 
    width: 100, 
    type: "boolean",
    sortable: true
  },
  { 
    field: 'FullControl', 
    headerName: 'Full Control', 
    width: 120, 
    type: "boolean",
    sortable: true
  },
  { 
    field: 'IsEditable', 
    headerName: 'Editable', 
    width: 100, 
    type: "boolean",
    sortable: true
  },
  { 
    field: 'post_order_for_worker_flag', 
    headerName: 'Post Order for Worker', 
    width: 180, 
    type: "boolean",
    sortable: true
  },
];



const handleSubmit = async (user: UserFormInput) => {
  const token: any = decode();
  const userId = token.userId;
  const companyId = token.companyId;

  console.log(user);

  try {
    const url = userToEdit ? `/api/users/${userToEdit.ID}` : '/api/users';
    const method = userToEdit ? 'PUT' : 'POST';

    const response = await apiClient(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit user');
    }

    const data = await response.json();
    const roleUrl = userToEdit ? `/api/users/role/${userToEdit.ID}` : '/api/users/role';

    if (user.role_id) {
      await apiClient(roleUrl, {
        method: userToEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: data.id,
          roleId: parseInt(user.role_id as any, 10),
        }),
      });
    }

   
    const ovidArray = Array.isArray(user.OVID) ? user.OVID : (user.OVID ? [user.OVID] : []);
    
    await apiClient('/api/users/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: data.id,
        OVID: ovidArray 
      }),
    });
    

    showSnackbar(userToEdit ? 'User updated successfully!' : 'User added successfully!', "success");
    refetch();
    setShowAddModal(false);
    setUserToEdit(null);
  } catch (err: any) {
    showSnackbar(err.message || "Failed to submit", "error");
  }
};



  const handleRowClick = (row: any) => {
    console.log('Row clicked:', row);
  };

  const handleExport = () => {
    console.log('Export clicked');
  };



  return (
    <PageContainer>

      <Header title="User Management" data={usersWithRoles}>
        <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
        <BulkImportLink onClick={() => setShowImportModal(true)}>Bulk Import</BulkImportLink>
      </Header>

      {showAddModal && (
        <ModalOverlay>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            <ModalHeader>{userToEdit ? "Edit" : "Add"} User</ModalHeader>
            <UserForm onSubmit={handleSubmit} defaultValues={userToEdit ?? undefined} onCancel={handleCloseModal} />
          </ModalContent>
        </ModalOverlay>
      )}

      {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <UserBulkImportComponent
              onClose={() => setShowImportModal(false)}
              onSuccess={() => refetch()}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showDeleteModal && userToDelete && (
        <ConfirmationModal
          title="Delete user?"
          message={`${userToDelete.first_name} ${userToDelete.last_name} will be removed from this list and will no longer be able to sign in.`}
          confirmText="Delete"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (isDeleting) return;
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          isLoading={isDeleting}
          loadingText="Deleting..."
        />
      )}

      <DataTable
        data={usersWithRoles}
        loading={loading}
        columns={columns}
        pageKey="users"
        defaultSort={[{ field: 'last_name', sort: 'asc' }]}
        title="Users"
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={(val) => setSelectedOvs(val)}
          defaultName="All Locations"
        />
      </DataTable>
    </PageContainer>
  );
};

export default UsersPage;